import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, PackageSearch } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/ErrorState';
import { fetchTrackedOrder } from '../../features/tracking/trackingThunks';
import { TrackingLayout } from './components/TrackingLayout';
import { TrackingSummary } from './components/TrackingSummary';
import { TrackingTimeline } from './components/TrackingTimeline';

/**
 * One parcel, in public.
 *
 * Reached from a search or straight from a link, so it fetches on mount and
 * assumes nothing about what came before it. When the order belongs to a
 * partner, their name replaces ours at the top: the person reading this bought
 * from them.
 */
export const TrackOrder = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { order, admin, isLoadingOrder, orderError } = useSelector((state) => state.tracking);

  useEffect(() => {
    dispatch(fetchTrackedOrder(orderId));
  }, [dispatch, orderId]);

  const partnerName = admin?.user
    ? [admin.user.first_name, admin.user.last_name].filter(Boolean).join(' ')
    : '';

  if (isLoadingOrder || (!order && !orderError)) {
    return (
      <TrackingLayout testId="track-order-page" title="Buscando tu pedido">
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="animate-spin text-ninja-green" />
        </div>
      </TrackingLayout>
    );
  }

  if (orderError) {
    return (
      <TrackingLayout testId="track-order-page" title="No pudimos mostrar el pedido">
        <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border">
          <ErrorState
            icon={PackageSearch}
            title="No pudimos mostrar el pedido"
            description={orderError}
            onRetry={() => dispatch(fetchTrackedOrder(orderId))}
            isRetrying={isLoadingOrder}
          >
            <Button variant="ghost" onClick={() => navigate('/tracking')}>
              Buscar otro pedido
            </Button>
          </ErrorState>
        </div>
      </TrackingLayout>
    );
  }

  return (
    <TrackingLayout
      testId="track-order-page"
      brand={partnerName || undefined}
      title="Tu pedido"
    >
      <TrackingSummary order={order} />
      <TrackingTimeline order={order} />
    </TrackingLayout>
  );
};
